import { useEffect, useState } from 'react';

import { Calendar, User, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

import { SettingsWrapper } from './SettingsWrapper';
import useUpdateCaseMetadata from '@/api/mutations/useUpdateCaseMetadata';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import Modal from '@/components/molecules/Modals/SettingsModal';
import formatDate from '@/helpers/formatDate';

interface CaseMetadataProps {
  caseId: string;
  caseName: string;
  createdDate: Date | undefined;
  createdBy: string;
}

export const CaseMetadata = ({ caseId, caseName, createdDate, createdBy }: CaseMetadataProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState(caseName || '');

  const { mutateAsync: updateCaseMetadata, isLoading } = useUpdateCaseMetadata();

  const onEditCaseName = async (updatedName: string) => {
    await updateCaseMetadata({ caseId, data: { name: updatedName } }).catch(() => {
      toast.error('Failed to update case name');
    });
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (caseName) {
      setName(caseName);
    }
  }, [caseName]);

  return (
    <SettingsWrapper>
      <div>
        <h2 className="mb-2 text-base font-semibold text-gray-900">Case Metadata</h2>
        <p className="mb-6 text-sm text-gray-500">View and update your case name and metadata information.</p>

        {/* Case Name Display */}
        <div className="mb-6 space-y-2">
          <Label className="text-sm font-medium text-gray-700">Case Name</Label>
          <div className="flex items-center gap-8">
            <div className="text-sm font-medium text-gray-900">{caseName}</div>
            <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)}>
              <Edit2 className="h-4 w-4" />
              Update Name
            </Button>
          </div>
        </div>

        {/* Read-only Metadata */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {createdDate && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Created</Label>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>{formatDate(createdDate, true, true)}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Created By</Label>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="h-4 w-4 text-gray-400" />
                <span>{createdBy || 'Unknown'}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Case ID</Label>
              <div className="font-mono text-sm text-gray-600">{caseId}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Update Name Modal */}
      <Modal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <Edit2 className="h-4 w-4 text-gray-700" />
            Update Case Name
          </div>
        }
        size="small"
        content={
          <div className="px-6 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Case Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter case name..."
                  className="w-full"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsModalOpen(false);
                    setName(caseName);
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isLoading || !name.trim()}
                  onClick={() => onEditCaseName(name)}
                >
                  {isLoading ? 'Updating...' : 'Update Name'}
                </Button>
              </div>
            </div>
          </div>
        }
      />
    </SettingsWrapper>
  );
};
